import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Checkbox } from '@/components/ui/checkbox'

export interface BulkUpdateField {
  key: string
  label: string
  type: 'text' | 'select' | 'number' | 'boolean' | 'date'
  options?: { value: string; label: string }[]
  placeholder?: string
}

interface BulkUpdateModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedCount: number
  entityType: string
  fields: BulkUpdateField[]
  onConfirm: (updates: Record<string, any>) => void
  isUpdating?: boolean
}

export function BulkUpdateModal({
  open,
  onOpenChange,
  selectedCount,
  entityType,
  fields,
  onConfirm,
  isUpdating = false,
}: BulkUpdateModalProps) {
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set())
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({})

  const handleFieldToggle = (fieldKey: string) => {
    setSelectedFields((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(fieldKey)) {
        newSet.delete(fieldKey)
        // Clear the value when unchecking
        setFieldValues((prevValues) => {
          const newValues = { ...prevValues }
          delete newValues[fieldKey]
          return newValues
        })
      } else {
        newSet.add(fieldKey)
      }
      return newSet
    })
  }

  const handleFieldValueChange = (fieldKey: string, value: any) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }))
  }

  const handleConfirm = () => {
    // Only send values for selected fields
    const updates: Record<string, any> = {}
    selectedFields.forEach((fieldKey) => {
      if (fieldValues[fieldKey] !== undefined) {
        updates[fieldKey] = fieldValues[fieldKey]
      }
    })
    onConfirm(updates)
  }

  const handleCancel = () => {
    setSelectedFields(new Set())
    setFieldValues({})
    onOpenChange(false)
  }

  const renderFieldInput = (field: BulkUpdateField) => {
    if (!selectedFields.has(field.key)) {
      return null
    }

    switch (field.type) {
      case 'select':
        return (
          <Select
            value={fieldValues[field.key] || ''}
            onValueChange={(value) => handleFieldValueChange(field.key, value)}
            aria-label={`Select new value for ${field.label}`}
          >
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || `Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'number':
        return (
          <Input
            type="number"
            value={fieldValues[field.key] || ''}
            onChange={(e) => handleFieldValueChange(field.key, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label}`}
            aria-label={`Enter new value for ${field.label}`}
          />
        )

      case 'date':
        return (
          <DatePicker
            value={fieldValues[field.key]}
            onChange={(v) => handleFieldValueChange(field.key, v ?? '')}
            aria-label={`Select new date for ${field.label}`}
          />
        )

      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              checked={fieldValues[field.key] || false}
              onCheckedChange={(checked) => handleFieldValueChange(field.key, checked)}
              aria-label={`Toggle ${field.label}`}
            />
            <span className="text-sm">Enable</span>
          </div>
        )

      case 'text':
      default:
        return (
          <Input
            type="text"
            value={fieldValues[field.key] || ''}
            onChange={(e) => handleFieldValueChange(field.key, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label}`}
            aria-label={`Enter new value for ${field.label}`}
          />
        )
    }
  }

  const hasSelectedFields = selectedFields.size > 0
  const hasValidValues =
    hasSelectedFields &&
    Array.from(selectedFields).every(
      (key) => fieldValues[key] !== undefined && fieldValues[key] !== ''
    )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto"
        aria-describedby="bulk-update-description"
      >
        <DialogHeader>
          <DialogTitle>Bulk Update {entityType}s</DialogTitle>
          <DialogDescription id="bulk-update-description">
            Update {selectedCount} selected {entityType}
            {selectedCount === 1 ? '' : 's'}. Select the fields you want to update and provide new
            values.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {fields.map((field) => (
            <div key={field.key} className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`field-${field.key}`}
                  checked={selectedFields.has(field.key)}
                  onCheckedChange={() => handleFieldToggle(field.key)}
                  aria-label={`Select ${field.label} for update`}
                />
                <Label
                  htmlFor={`field-${field.key}`}
                  className="text-sm font-medium cursor-pointer"
                >
                  {field.label}
                </Label>
              </div>
              {renderFieldInput(field)}
            </div>
          ))}

          {!hasSelectedFields && (
            <p className="text-sm text-muted-foreground italic">
              Select at least one field to update
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isUpdating}
            aria-label="Cancel bulk update"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!hasValidValues || isUpdating}
            aria-label={`Confirm bulk update of ${selectedCount} ${entityType}${selectedCount === 1 ? '' : 's'}`}
          >
            {isUpdating
              ? 'Updating...'
              : `Update ${selectedCount} ${entityType}${selectedCount === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
